import React, { useEffect, useState, useMemo } from 'react';
import { Sparkles, Building2, TrendingUp, MapPin, Loader2, ChevronDown, ChevronUp, Globe, Layers } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, RadarChart, PolarGrid, PolarAngleAxis, Radar } from 'recharts';
import { IntelligenceLayout, fetchWithAuth, MetricCard, useCompanyFilters, CompanyFilterBar, CompanyTypeBadge } from './IntelligenceLayout';
import { WatchlistButton } from '../components/WatchlistButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_STYLES = {
  'Very Likely': 'bg-green-100 text-green-700 border-green-200',
  'Likely': 'bg-blue-100 text-blue-700 border-blue-200',
  'Possible': 'bg-amber-100 text-amber-700 border-amber-200',
  'Unlikely': 'bg-gray-100 text-gray-500 border-gray-200',
};

const FutureHiring = ({ embedded = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('probability_score');
  const [sortDir, setSortDir] = useState('desc');
  const [tierFilter, setTierFilter] = useState('all');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const { excludeStaffing, setExcludeStaffing, excludeRetail, setExcludeRetail, filterParams } = useCompanyFilters();

  useEffect(() => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/intelligence/future-hiring?limit=200${filterParams}`)
      .then(d => setData(d)).catch(() => {}).finally(() => setLoading(false));
  }, [excludeStaffing, excludeRetail]);

  const handleSort = (f) => {
    if (sortField === f) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(f); setSortDir('desc'); }
  };

  const companies = data?.companies || [];
  const filtered = tierFilter === 'all' ? companies : companies.filter(c => c.probability_tier === tierFilter);
  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    const m = sortDir === 'desc' ? -1 : 1;
    if (sortField === 'company') return m * a.company.localeCompare(b.company);
    return m * ((a[sortField] || 0) - (b[sortField] || 0));
  }), [filtered, sortField, sortDir]);

  const tiers = data?.tier_distribution || {};
  const chartData = companies.slice(0, 15).map(c => ({
    company: c.company.length > 16 ? c.company.slice(0, 14) + '..' : c.company,
    score: c.probability_score,
  }));

  const SortHeader = ({ field, label, align = 'left' }) => (
    <th className={`text-${align} px-3 py-3 cursor-pointer hover:text-[#0A1F44] select-none whitespace-nowrap text-xs`}
      onClick={() => handleSort(field)}>
      <span className="inline-flex items-center gap-1">{label}
        {sortField === field && (sortDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
      </span>
    </th>
  );

  return (
    <IntelligenceLayout title="Future Hiring" embedded={embedded}>
      <header className="bg-white border-b border-gray-200 px-8 py-5 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-green-50 rounded-xl flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="future-hiring-title">Future Hiring Predictor</h1>
            <p className="text-sm text-gray-500">Companies likely to increase hiring based on pattern analysis</p>
          </div>
        </div>
      </header>

      <main className="p-8 space-y-6">
        <CompanyFilterBar excludeStaffing={excludeStaffing} setExcludeStaffing={setExcludeStaffing}
          excludeRetail={excludeRetail} setExcludeRetail={setExcludeRetail} />
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-2 text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /> Analyzing hiring patterns...</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="future-metrics">
              <MetricCard label="Very Likely" value={tiers['Very Likely'] || 0} icon={TrendingUp} subtext="Strong growth signals" />
              <MetricCard label="Likely" value={tiers['Likely'] || 0} icon={Sparkles} />
              <MetricCard label="Possible" value={tiers['Possible'] || 0} icon={Globe} />
              <MetricCard label="Total Analyzed" value={data?.total_analyzed || 0} icon={Building2} />
            </div>

            <div className="flex items-center gap-3" data-testid="future-filters">
              <span className="text-sm text-gray-600 font-medium">Probability:</span>
              {['all', 'Very Likely', 'Likely', 'Possible', 'Unlikely'].map(tier => (
                <button key={tier} onClick={() => setTierFilter(tier)} data-testid={`future-filter-${tier.toLowerCase().replace(' ', '-')}`}
                  className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${tierFilter === tier ? 'bg-[#0A1F44] text-white shadow-md' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}>
                  {tier === 'all' ? `All (${companies.length})` : `${tier} (${tiers[tier] || 0})`}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="future-chart">
                <h3 className="text-sm font-semibold text-[#0A1F44] mb-4">Future Hiring Probability — Top Companies</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="company" width={120} tick={{ fontSize: 9 }} />
                    <Tooltip />
                    <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                      {chartData.map((_, i) => (
                        <Cell key={i} fill={i < 3 ? '#16a34a' : i < 8 ? '#2563eb' : '#d97706'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {selectedCompany && (
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="signal-radar">
                  <h3 className="text-sm font-semibold text-[#0A1F44] mb-2">{selectedCompany.company} — Signal Breakdown</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <RadarChart data={[
                      { signal: 'Gradual Increase', value: selectedCompany.signals.gradual_increase },
                      { signal: 'Geo Expansion', value: selectedCompany.signals.geographic_expansion },
                      { signal: 'Multi-Dept', value: selectedCompany.signals.multi_department },
                      { signal: 'MoM Growth', value: selectedCompany.signals.mom_growth },
                    ]}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="signal" tick={{ fontSize: 10 }} />
                      <Radar dataKey="value" fill="#16a34a" fillOpacity={0.3} stroke="#16a34a" />
                    </RadarChart>
                  </ResponsiveContainer>
                  <div className="flex gap-2 mt-2 text-xs text-gray-500">
                    <span className="px-2 py-1 bg-gray-50 rounded">{selectedCompany.signal_labels.geo}</span>
                    <span className="px-2 py-1 bg-gray-50 rounded">{selectedCompany.signal_labels.dept}</span>
                    <span className="px-2 py-1 bg-gray-50 rounded">{selectedCompany.signal_labels.growth}</span>
                  </div>
                </div>
              )}
              {!selectedCompany && (
                <div className="bg-gradient-to-br from-green-50 to-blue-50 border border-green-200 rounded-xl p-6 flex flex-col justify-center">
                  <Sparkles className="h-8 w-8 text-green-500 mb-3" />
                  <h3 className="text-sm font-bold text-[#0A1F44] mb-2">How Predictions Work</h3>
                  <div className="space-y-2 text-xs text-gray-600">
                    <p><strong>Gradual Increase (25pts)</strong> — Job postings increasing over time</p>
                    <p><strong>Geographic Expansion (25pts)</strong> — Hiring across multiple cities/states</p>
                    <p><strong>Multi-Department (25pts)</strong> — Hiring across different departments</p>
                    <p><strong>MoM Growth (25pts)</strong> — Recent acceleration in posting activity</p>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-3">Click a company row to see its signal radar chart</p>
                </div>
              )}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm" data-testid="future-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 uppercase border-b border-gray-100 bg-gray-50/30">
                      <th className="text-left px-3 py-3 w-8">#</th>
                      <SortHeader field="company" label="Company" />
                      <SortHeader field="probability_score" label="Score" align="right" />
                      <th className="text-center px-3 py-3">Prediction</th>
                      <SortHeader field="total_jobs_30d" label="Jobs (30d)" align="right" />
                      <SortHeader field="jobs_7d" label="Jobs (7d)" align="right" />
                      <SortHeader field="cities_count" label="Cities" align="right" />
                      <SortHeader field="departments_count" label="Depts" align="right" />
                      <th className="text-left px-3 py-3">Industry</th>
                      <th className="text-center px-3 py-3">Geo</th>
                      <th className="text-center px-3 py-3">Growth</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((item, i) => (
                      <tr key={item.company} className={`border-t border-gray-100 cursor-pointer transition-colors ${selectedCompany?.company === item.company ? 'bg-green-50' : 'hover:bg-[#f8f6f0]'}`}
                        onClick={() => setSelectedCompany(item)} data-testid={`future-row-${i}`}>
                        <td className="px-3 py-3 text-xs text-gray-400">{i + 1}</td>
                        <td className="px-3 py-3"><div className="flex items-center gap-2"><WatchlistButton company={item.company} sourceModule="future-hiring" /><span className="font-semibold text-[#0A1F44]">{item.company}</span></div></td>
                        <td className="px-3 py-3 text-right"><span className="font-bold text-[#0A1F44]">{item.probability_score}</span></td>
                        <td className="px-3 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-[10px] font-bold border ${TIER_STYLES[item.probability_tier] || TIER_STYLES['Unlikely']}`}>{item.probability_tier}</span>
                        </td>
                        <td className="px-3 py-3 text-right text-gray-600">{item.total_jobs_30d}</td>
                        <td className="px-3 py-3 text-right font-medium text-green-700">{item.jobs_7d}</td>
                        <td className="px-3 py-3 text-right text-gray-600">{item.cities_count}</td>
                        <td className="px-3 py-3 text-right text-gray-600">{item.departments_count}</td>
                        <td className="px-3 py-3">{item.industry ? <div className="flex flex-col gap-1"><span className="px-2 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600 border border-gray-200 w-fit">{item.industry}</span><CompanyTypeBadge type={item.company_type} /></div> : '—'}</td>
                        <td className="px-3 py-3 text-center text-[10px] font-medium text-blue-600">{item.signal_labels.geo}</td>
                        <td className="px-3 py-3 text-center text-[10px] font-medium text-green-600">{item.signal_labels.growth}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
    </IntelligenceLayout>
  );
};

export default FutureHiring;
